export function formatDate(value) {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d?.getTime?.())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Compact MM/DD/YY — used in dense list views (Plants, Suppliers,
// Parts, Team) where the full "Apr 22, 2026" would widen the column
// unnecessarily. Locale is pinned to en-US so the order is always
// month/day/year regardless of the browser locale, matching how
// Proxemics writes dates in their paperwork.
export function formatDateShort(value) {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d?.getTime?.())) return '';
  return d.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateTime(value) {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d?.getTime?.())) return '';
  return d.toLocaleString();
}

export function formatCurrency(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(Number(amount));
}

export function formatHours(hours) {
  if (hours == null) return '';
  return `${Number(hours).toFixed(2)} h`;
}

/**
 * Display a phone number.
 *
 * Two paths:
 *   - US / Canada (no leading "+"):  "(XXX) XXX-XXXX"
 *   - International (leading "+"):   "+CC CC CCCC CCCC" (2/2/4/4 groups,
 *     spilling any extra digits into the last group). Country-specific
 *     groupings vary wildly, so this is a display rule — not a locale-
 *     aware parser. It matches the shape Jillian asked for.
 *
 * If we can't confidently format the value (e.g. an 8-digit scrap), we
 * return the original string unchanged so a legacy oddity still renders
 * rather than going blank.
 */
export function formatPhone(value) {
  if (!value) return '';
  const s = String(value).trim();

  // Peel off a trailing extension if present. Markers are `x`, `ext`,
  // `ext.`, or `#`, anchored to the end of the string so a stray `x`
  // somewhere in the middle of a value can't accidentally trigger
  // ext-mode. Stored values from formatPhoneInput's ext-aware branch
  // come in as "(XXX) XXX-XXXX ext. 123" / "+CC ... ext. 123", and
  // we keep that canonical shape on display.
  const extMatch = s.match(/^(.*?)\s*(?:x|ext\.?|#)\s*(\d+)\s*$/i);
  if (extMatch && extMatch[1].trim()) {
    const formattedMain = formatPhoneMainDisplay(extMatch[1].trim());
    return `${formattedMain} ext. ${extMatch[2]}`;
  }

  return formatPhoneMainDisplay(s);
}

// Display-side formatter for just the main number portion. Falls back
// to the original string when it can't confidently format (e.g. an
// 8-digit scrap) so legacy oddities still render rather than going
// blank — same forgiving behavior the original formatPhone had.
//
// International numbers (anything containing a `+`) are shown
// verbatim — the input-side formatter already normalized whatever
// grouping the user typed into a canonical "+CC ..." form, and
// forcing a rigid 2/2/4/4 grouping at display time mangles common
// country shapes (US +1, UK +44 trunk, etc.). Letting the stored
// value flow through means whatever the user typed is what they see.
function formatPhoneMainDisplay(s) {
  if (!s) return '';
  if (s.includes('+')) return s.trim();
  let digits = s.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return s;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Format in-flight phone-input keystrokes to match formatPhone's shape.
 * Used by PhoneField so what the user sees (and what we eventually save)
 * is always the canonical display format. Partial input is kept so the
 * caret position stays sensible:
 *   "5"                  → "(5"
 *   "5551234"            → "(555) 123-4"
 *   "+"                  → "+"
 *   "+1 555 123 4567"    → "+1 555 123 4567"   (user's grouping preserved)
 *   "+1 (555) 123-4567"  → "+1 555 123 4567"   (separators normalized)
 *   "+44 79 1112 3456"   → "+44 79 1112 3456"
 *   "+447911123456"      → "+447911123456"     (no spaces in, none out)
 *   "(52) 818-280-1197"  → "+52 818 280 1197"  (12 digits → international)
 *
 * International mode is triggered by EITHER a `+` anywhere in the
 * input OR by more digits than a US number can hold (>10, excluding
 * the US-with-country-code-1 case). The "extra digits" heuristic
 * catches the realistic case of pasting a foreign number without
 * remembering to add `+` — the old behavior silently truncated to
 * the first 10 digits, which felt like the field was rejecting the
 * number entirely. In international mode we preserve whatever
 * spacing the user typed and convert common separators (parens,
 * dashes, dots) to spaces. A US-with-leading-1 paste is still
 * stripped to its 10-digit canonical form.
 */
export function formatPhoneInput(value, options = {}) {
  if (value == null) return '';
  const s = String(value);

  // Extension handling — only when the caller opts in. Office Phone
  // fields pass `{ allowExtension: true }`; cell phones don't, so a
  // stray `x` in a cell-phone field still gets stripped as a non-digit
  // and doesn't trigger ext-mode. The `\d*` (not `\d+`) lets us match
  // mid-typing too — the user typing the bare marker preserves a
  // trailing " ext. " so they can keep typing the extension digits
  // without the formatter eating their `x`.
  if (options.allowExtension) {
    const extMatch = s.match(/^(.*?)\s*(?:x|ext\.?|#)\s*(\d*)\s*$/i);
    if (extMatch && extMatch[1].trim()) {
      const mainPart = extMatch[1];
      // Cap the extension at 8 digits — generous for real-world PBXs
      // and stops a held-down keypad from running the cursor away.
      const extPart = extMatch[2].slice(0, 8);
      const formattedMain = formatPhoneMainInput(mainPart);
      return extPart
        ? `${formattedMain} ext. ${extPart}`
        : `${formattedMain} ext. `;
    }
  }

  return formatPhoneMainInput(s);
}

// Input-side formatter for just the main number portion. Two modes:
//
//   • International (permissive) — triggered by either an explicit
//     `+` anywhere in the value OR by more digits than a US number
//     can hold. The "more digits" heuristic catches the realistic
//     case of pasting "(52) 818-280-1197" (12 digits, no `+`) for a
//     Mexican number — the old behavior silently truncated to 10
//     digits and reformatted as a US number, dropping the trailing
//     two digits. A 11-digit value starting with "1" is exempted
//     because that's a US number with the country code prefix the
//     user wants stripped, not international.
//
//   • US auto-format — the canonical "(XXX) XXX-XXXX" path. Partial
//     input keeps the caret happy ("5" → "(5", etc.).
//
// In international mode we preserve the user's own spacing (so
// "+44 20 7946 0958", "+1 555 123 4567", and "(52) 818-280-1197"
// all survive intact) and convert common separators — parens,
// dashes, dots — to single spaces rather than collapsing the visual
// structure.
function formatPhoneMainInput(value) {
  const s = String(value || '');
  const digits = s.replace(/\D/g, '');
  const usWithCountryCode = digits.length === 11 && digits.startsWith('1');
  const looksInternational =
    s.includes('+') || (digits.length > 10 && !usWithCountryCode);

  if (looksInternational) {
    const body = s
      .replace(/\+/g, '') //               drop any "+"; we re-add a single leader below
      .replace(/[().\-]/g, ' ') //         common separators → space
      .replace(/[^\d ]/g, '') //           strip stray letters / other punctuation
      .replace(/ {2,}/g, ' ') //           collapse runs of whitespace
      .replace(/^ +/, ''); //              strip leading spaces (the "+" is the leader)
    return `+${body}`;
  }

  let usDigits = digits;
  if (usWithCountryCode) usDigits = usDigits.slice(1);
  usDigits = usDigits.slice(0, 10);
  if (!usDigits) return '';
  if (usDigits.length <= 3) return `(${usDigits}`;
  if (usDigits.length <= 6) return `(${usDigits.slice(0, 3)}) ${usDigits.slice(3)}`;
  return `(${usDigits.slice(0, 3)}) ${usDigits.slice(3, 6)}-${usDigits.slice(6)}`;
}

/**
 * Collapse the structured address columns
 * (Street / City / State / Postal / Country) into a single
 * one-line string suitable for read-side detail cards. Standard
 * US/Canada visual order — "Street, City, State Postal, Country" —
 * with State and Postal joined by a single space (the postal
 * convention) and everything else by ", ". Empty pieces drop out
 * gracefully so a partial address still renders cleanly.
 *
 * Returns an empty string if every piece is blank — callers can use
 * that to decide whether to render a "—" placeholder or omit the
 * card entirely.
 */
export function formatAddressLine({ street, city, state, postalCode, country } = {}) {
  const trim = (v) => (v == null ? '' : String(v).trim());
  const s = trim(street);
  const c = trim(city);
  const st = trim(state);
  const pc = trim(postalCode);
  const co = formatCountry(country);
  // State + postal go together — "ON K8N 5T3" reads as one block.
  const stateZip = [st, pc].filter(Boolean).join(' ');
  return [s, c, stateZip, co].filter(Boolean).join(', ');
}

/**
 * Normalise a country value for DISPLAY only — "United States" (or
 * "United States of America") reads as "USA" everywhere it's shown, while
 * every other country passes through unchanged. The stored data is left
 * as-is; this is purely how it renders.
 */
export function formatCountry(country) {
  const c = (country == null ? '' : String(country)).trim();
  return /^united states( of america)?$/i.test(c) ? 'USA' : c;
}

// (The old `formatInternationalDigits` helper that imposed a rigid
// 2/2/4/4 grouping was removed — it mangled common country shapes
// like US +1 ("+1 555 123 4567" became "+15 55 5123 567"). The
// input/display formatters now preserve whatever spacing the user
// types after the "+".)
